// src/components/ManageCoordinatorsPage.js
//
// Admin page to manage the `coordinators` collection (add / edit / deactivate).
// Replaces the old hardcoded coordinator name arrays. Soft-delete only — a
// coordinator is never removed, and existing patient assignments (encoded in
// the patient `status` string, e.g. "Pending (Levi)") are never touched.

import { useEffect, useState } from "react";
import { fetchSheetPatients } from "../services/sheetPatientsService";
import { useLocation, useNavigate } from "react-router-dom";
import { backTarget } from "../utils/backTarget";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import { getConsentsPath, getDuplicatesPath } from "../services/firestorePaths";
import { activeSiteUsesLegacySheet } from "../services/activeTenant";
import {
  fetchCoordinators,
  createCoordinator,
  updateCoordinator,
  deactivateCoordinator,
  reactivateCoordinator,
} from "../services/coordinatorService";
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trophy,
  Mail,
  MailX,
  Clock,
  ClockAlert,
  UserCheck,
  UserX,
  X,
  Save,
} from "lucide-react";

// Status prefixes that embed a coordinator name as "Prefix (Name)".
const ASSIGNMENT_PREFIXES = [
  "Pending",
  "Coordinator to Screen",
  "Schedule with Coordinator",
];

const extractCoordinator = (status) => {
  if (!status) return null;
  const m = String(status).match(/\((.*?)\)$/);
  return m ? m[1].trim() : null;
};

const emptyForm = {
  name: "",
  email: "",
  includeInLeaderboard: true,
  receivesUrgentEmail: true,
  receivesStaleBoxReminders: true,
};

export default function ManageCoordinatorsPage() {
  const navigate = useNavigate();
  // Back: home when opened from the home page, else the Admin Dashboard.
  const back = backTarget(useLocation().state, "/dashboard", "Dashboard");
  const [coordinators, setCoordinators] = useState([]);
  const [assignedCounts, setAssignedCounts] = useState({}); // name -> # patients
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Modal state: { mode: "add" | "edit", form, id?, previousName? }
  const [modal, setModal] = useState(null);

  const loadCoordinators = async () => {
    const list = await fetchCoordinators();
    setCoordinators(list);
  };

  // Best-effort count of how many patients are currently assigned to each
  // coordinator (so deactivation can warn the admin). Never blocks the page.
  const loadAssignedCounts = async () => {
    try {
      const counts = {};
      const tally = (status) => {
        const name = extractCoordinator(status);
        if (!name) return;
        // Only count recognized assignment statuses.
        const ok = ASSIGNMENT_PREFIXES.some((p) =>
          String(status).startsWith(p + " (")
        );
        if (!ok) return;
        counts[name] = (counts[name] || 0) + 1;
      };

      // Google Sheet patients (Dubois-only legacy store); skip on other sites.
      if (activeSiteUsesLegacySheet()) {
        try {
          const sheet = await fetchSheetPatients();
          (sheet || []).forEach((p) => tally(p.status));
        } catch (e) {
          console.warn("Could not load sheet patients for counts:", e);
        }
      }

      // Firestore patients
      const consentsSnap = await getDocs(collection(db, getConsentsPath()));
      consentsSnap.docs.forEach((d) => tally(d.data().status));
      const dupSnap = await getDocs(collection(db, getDuplicatesPath()));
      dupSnap.docs.forEach((d) => tally(d.data().status));

      setAssignedCounts(counts);
    } catch (e) {
      console.warn("Assigned-count tally failed (non-blocking):", e);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadCoordinators();
      setLoading(false);
      loadAssignedCounts(); // fire-and-forget
    })();
  }, []);

  const openAdd = () =>
    setModal({ mode: "add", form: { ...emptyForm } });

  const openEdit = (c) =>
    setModal({
      mode: "edit",
      id: c.id,
      previousName: c.name,
      form: {
        name: c.name || "",
        email: c.email || "",
        includeInLeaderboard: c.includeInLeaderboard !== false,
        receivesUrgentEmail: c.receivesUrgentEmail !== false,
        receivesStaleBoxReminders: c.receivesStaleBoxReminders !== false,
      },
    });

  const closeModal = () => setModal(null);

  const handleSave = async () => {
    const name = modal.form.name.trim();
    if (!name) {
      alert("Coordinator name is required.");
      return;
    }
    // Prevent duplicate names (case-insensitive), except the one being edited.
    const clash = coordinators.find(
      (c) =>
        c.name?.toLowerCase() === name.toLowerCase() &&
        c.id !== modal.id
    );
    if (clash) {
      alert(`A coordinator named "${name}" already exists.`);
      return;
    }

    setSaving(true);
    try {
      if (modal.mode === "add") {
        const maxOrder = coordinators.reduce(
          (m, c) => Math.max(m, c.order ?? 0),
          0
        );
        await createCoordinator({ ...modal.form, name, order: maxOrder + 1 });
      } else {
        await updateCoordinator(
          modal.id,
          { ...modal.form, name },
          modal.previousName
        );
      }
      await loadCoordinators();
      closeModal();
    } catch (e) {
      console.error("Save failed:", e);
      alert("Could not save coordinator. See console for details.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (c) => {
    const n = assignedCounts[c.name] || 0;
    const msg =
      n > 0
        ? `${c.name} still has ${n} patient(s) assigned.\n\n` +
          `Deactivating only HIDES ${c.name} from the assignment dropdowns and ` +
          `leaderboard going forward. Those ${n} patient(s) keep their current ` +
          `assignment and records — nothing is deleted. You can reactivate later.\n\nContinue?`
        : `Deactivate ${c.name}? They'll be hidden from dropdowns and the ` +
          `leaderboard, but nothing is deleted and you can reactivate later.`;
    if (!window.confirm(msg)) return;
    try {
      await deactivateCoordinator(c.id);
      await loadCoordinators();
    } catch (e) {
      console.error("Deactivate failed:", e);
      alert("Could not deactivate. See console.");
    }
  };

  const handleReactivate = async (c) => {
    try {
      await reactivateCoordinator(c.id);
      await loadCoordinators();
    } catch (e) {
      console.error("Reactivate failed:", e);
      alert("Could not reactivate. See console.");
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0820] text-slate-100 px-4 py-6 sm:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(back.to)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-sm text-slate-200 transition"
          >
            <ArrowLeft className="w-4 h-4" />
            {back.label}
          </button>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">
              Manage Coordinators
            </h1>
            <p className="text-[12px] text-slate-400">
              Add, edit, or deactivate coordinators. Changes flow to the
              leaderboard, patient statuses, and urgent-email routing.
            </p>
          </div>
          <button
            onClick={openAdd}
            className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500/40 to-blue-500/40 border border-purple-300/30 hover:from-purple-500/60 hover:to-blue-500/60 text-sm font-medium text-white transition"
          >
            <Plus className="w-4 h-4" />
            Add Coordinator
          </button>
        </div>

        {loading ? (
          <div className="text-slate-400 text-sm py-12 text-center">
            Loading coordinators…
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl overflow-hidden">
            {/* Column headers */}
            <div className="hidden sm:grid grid-cols-[1fr_1.4fr_auto_auto_auto_auto] gap-3 px-4 py-2.5 border-b border-white/5 text-[10px] uppercase tracking-[0.18em] text-slate-400">
              <span>Coordinator</span>
              <span>Email</span>
              <span className="text-center w-24">Leaderboard</span>
              <span className="text-center w-24">Urgent Email</span>
              <span className="text-center w-24">Box Reminders</span>
              <span className="text-center w-32">Actions</span>
            </div>

            {coordinators.map((c) => {
              const inactive = c.active === false;
              const n = assignedCounts[c.name] || 0;
              return (
                <div
                  key={c.id}
                  className={`grid grid-cols-1 sm:grid-cols-[1fr_1.4fr_auto_auto_auto_auto] gap-2 sm:gap-3 items-center px-4 py-3 border-b border-white/5 last:border-none ${
                    inactive ? "opacity-50" : ""
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500 flex items-center justify-center text-[11px] font-bold text-white ring-1 ring-white/20 shrink-0">
                      {c.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-white truncate">
                        {c.name}
                        {inactive && (
                          <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-400">
                            (inactive)
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-400">
                        {n} assigned
                      </div>
                    </div>
                  </div>

                  <div className="text-[13px] text-slate-300 truncate">
                    {c.email || (
                      <span className="text-slate-500 italic">no email</span>
                    )}
                  </div>

                  <div className="flex sm:justify-center">
                    {c.includeInLeaderboard !== false ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-amber-300">
                        <Trophy className="w-3.5 h-3.5" /> Yes
                      </span>
                    ) : (
                      <span className="text-[11px] text-slate-500">No</span>
                    )}
                  </div>

                  <div className="flex sm:justify-center">
                    {c.receivesUrgentEmail !== false ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-sky-300">
                        <Mail className="w-3.5 h-3.5" /> Yes
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                        <MailX className="w-3.5 h-3.5" /> No
                      </span>
                    )}
                  </div>

                  <div className="flex sm:justify-center">
                    {c.receivesStaleBoxReminders !== false ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-300">
                        <Clock className="w-3.5 h-3.5" /> Yes
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                        <ClockAlert className="w-3.5 h-3.5" /> No
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5 sm:justify-center">
                    <button
                      onClick={() => openEdit(c)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-[12px] text-slate-200 transition"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      Edit
                    </button>
                    {inactive ? (
                      <button
                        onClick={() => handleReactivate(c)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-300/30 hover:bg-emerald-500/25 text-[12px] text-emerald-200 transition"
                      >
                        <UserCheck className="w-3.5 h-3.5" />
                        Reactivate
                      </button>
                    ) : (
                      <button
                        onClick={() => handleDeactivate(c)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-rose-500/15 border border-rose-300/30 hover:bg-rose-500/25 text-[12px] text-rose-200 transition"
                      >
                        <UserX className="w-3.5 h-3.5" />
                        Deactivate
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="mt-4 text-[11px] text-slate-500 flex items-center gap-1.5">
          <Trophy className="w-3 h-3 text-amber-300/70" />
          Deactivating never deletes data — existing patient assignments and
          leaderboard counts are preserved.
        </p>
      </div>

      {/* Add / Edit modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#120e2e] shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
              <h2 className="text-sm font-semibold text-white">
                {modal.mode === "add" ? "Add Coordinator" : "Edit Coordinator"}
              </h2>
              <button
                onClick={closeModal}
                className="p-1 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={modal.form.name}
                  onChange={(e) =>
                    setModal((m) => ({
                      ...m,
                      form: { ...m.form, name: e.target.value },
                    }))
                  }
                  placeholder="e.g. Chris"
                  className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 text-sm text-slate-100 focus:outline-none focus:border-purple-400/50"
                />
                {modal.mode === "edit" && (
                  <p className="mt-1 text-[10px] text-slate-500">
                    Renaming keeps a history of prior names so existing patient
                    statuses are never orphaned.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">
                  Email (for urgent alerts)
                </label>
                <input
                  type="email"
                  value={modal.form.email}
                  onChange={(e) =>
                    setModal((m) => ({
                      ...m,
                      form: { ...m.form, email: e.target.value },
                    }))
                  }
                  placeholder="name@clinicalresearchassoc.com"
                  className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 text-sm text-slate-100 focus:outline-none focus:border-purple-400/50"
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={modal.form.includeInLeaderboard}
                  onChange={(e) =>
                    setModal((m) => ({
                      ...m,
                      form: {
                        ...m.form,
                        includeInLeaderboard: e.target.checked,
                      },
                    }))
                  }
                  className="w-4 h-4 accent-amber-400"
                />
                <span className="text-sm text-slate-200">
                  Include in leaderboard
                </span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={modal.form.receivesUrgentEmail}
                  onChange={(e) =>
                    setModal((m) => ({
                      ...m,
                      form: {
                        ...m.form,
                        receivesUrgentEmail: e.target.checked,
                      },
                    }))
                  }
                  className="w-4 h-4 accent-sky-400"
                />
                <span className="text-sm text-slate-200">
                  Receives urgent-patient emails
                </span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={modal.form.receivesStaleBoxReminders}
                  onChange={(e) =>
                    setModal((m) => ({
                      ...m,
                      form: {
                        ...m.form,
                        receivesStaleBoxReminders: e.target.checked,
                      },
                    }))
                  }
                  className="w-4 h-4 accent-emerald-400"
                />
                <span className="text-sm text-slate-200">
                  Receives stale coordinator-box reminders
                </span>
              </label>
            </div>

            <div className="px-5 py-3 border-t border-white/10 flex justify-end gap-2">
              <button
                onClick={closeModal}
                disabled={saving}
                className="px-4 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-sm text-slate-200 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500/50 to-blue-500/50 border border-purple-300/30 hover:from-purple-500/70 hover:to-blue-500/70 text-sm font-medium text-white transition disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
